import { Inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

export interface TokenInfo {
    value: string;
    expiry: number;
    isThirdParty: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class TokenService {
    token: any;
    response: any;
    private readonly TOKEN_KEY = 'apiToken';
    private readonly TOKEN_EXPIRY_MS = 3600000; // 1 hour

    constructor(
        // Inject the single shared OktaAuth instance created in main.ts.
        // Previously a second OktaAuth was instantiated here, which created an
        // isolated TokenManager that didn't share state with the rest of the app.
        @Inject(OKTA_AUTH) private authClient: OktaAuth
    ) {}

    setToken(token: string, isThirdParty: boolean = false): void {
        if (!token) return;

        const tokenInfo: TokenInfo = {
            value: token,
            expiry: Date.now() + this.TOKEN_EXPIRY_MS,
            isThirdParty
        };

        localStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokenInfo));
    }

    getToken(): string | null {
        const tokenStr = localStorage.getItem(this.TOKEN_KEY);
        if (!tokenStr) return null;

        try {
            const tokenInfo: TokenInfo = JSON.parse(tokenStr);

            if (Date.now() > tokenInfo.expiry) {
                this.clearToken();
                return null;
            }

            return tokenInfo.value;
        } catch {
            this.clearToken();
            return null;
        }
    }

    isThirdPartyToken(): boolean {
        const tokenStr = localStorage.getItem(this.TOKEN_KEY);
        if (!tokenStr) return false;

        try {
            const tokenInfo: TokenInfo = JSON.parse(tokenStr);
            return tokenInfo.isThirdParty && Date.now() <= tokenInfo.expiry;
        } catch {
            return false;
        }
    }

    clearToken(): void {
        localStorage.removeItem(this.TOKEN_KEY);
    }

    hasValidToken(): boolean {
        return this.getToken() !== null;
    }

    async getTokens(refresh = false) {
        this.token = this.authClient.getAccessToken()?.toString();
        if (this.tokenExpired(this.token) || refresh) {
            try {
                // Attempt silent token renewal via a hidden /authorize iframe.
                // This works in a normal browser tab but will be blocked when
                // Hestia is embedded inside the Amazon Connect CCP iframe due to
                // third-party cookie restrictions and browser framing policies.
                this.response = await this.authClient.token.getWithoutPrompt({
                    responseType: 'token',
                });
                return this.response.tokens;
            } catch (error) {
                // Silent renew failed — most likely because we are running
                // inside the Amazon Connect iframe where the hidden /authorize
                // iframe is blocked. Fall back to the tokens that are currently
                // stored in the shared TokenManager so the app keeps working
                // until OktaSessionService detects expiry and forces logout.
                console.warn('Silent token renewal failed (possibly inside iframe), using stored tokens:', error instanceof Error ? error.message : 'Unknown error');
                return this.authClient.tokenManager.getTokens();
            }
        }
        return this.authClient.tokenManager.getTokens();
    }

    private tokenExpired(token: string) {
        if (token) {
            const expiry = JSON.parse(atob(token.split('.')[1])).exp;
            let currdate = Math.floor(new Date().getTime() / 1000);
            return currdate >= expiry - 1800;
        }
        return true;
    }
}
